import { ApolloError, useMutation } from "@apollo/client";
import moment from "moment";
import { useEffect, useState } from "react";
import Toggle from 'react-toggle'
import ReactTooltip from "react-tooltip";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import { Flex, Heading, Card, Text, Box, Button } from "rebass/styled-components";
import styled from "styled-components";
import Modal from "styled-react-modal";
import { toast } from 'react-toastify';
import { NoUpcomings } from "../../components/icons/NoUpcomings";
import { Skeleton } from "../../components/loadingSkeleton/Skeleton";
import SendSms from "../../components/SendSms";
import { REPORT_NO_SHOW } from "../../graphql/mutations";
import { GET_PATIENT_CHART_ALERTS, GET_UPCOMINGS } from "../../graphql/queries";
import { IReportNoShowOutput, IReportNoShowInput } from "../../graphql/types/mutations/appointment";
import { ICalendlyEvent, IEventInvitees, IGetUpcominsOutput, IUpcoming } from "../../graphql/types/queries/appointment";
import { IUser } from "../../graphql/types/queries/user";
import useInterval from "../../hooks/useInterval";
import withSession from "../../lib/withSession";
import { Constants } from "../../utils/Constants";
import { formatPhoneNumber } from "../../utils/formatPhoneNumber";
import { getMockedEmail, getMockedFirstName, getMockedLastName, getMockedPhone } from "../../utils/mocks";
import { ToolbarMenuButton } from "../messages/components/ConversationToolbar";
import { PatientInner } from "../Patient";
import { TalkingPoints } from "./TalkingPoints";
import { useNavigate } from "react-router-dom";


const VerticalTimelineElementCustom = styled(VerticalTimelineElement)`
  margin-bottom: 0 !important;
  margin-top: 12px !important;
  &:first-child {
    margin-top: 0px !important
  }
  & > .vertical-timeline-element-content {
  background-color: transparent !important;
  background: transparent !important;
  }
`

const VerticalTimelineElementCustomFilter = styled(VerticalTimelineElement)`
  background-color: transparent;
  box-shadow: initial;
  margin-bottom: 0 !important;
  margin-top: 12px !important;
  &:first-child {
    margin-top: 0px !important
  }
  & > .vertical-timeline-element-content {
  background-color: transparent !important;
  box-shadow: initial !important;
  }
`

const Wrapper = styled(Flex)`
  overflow: hidden;
  flex-direction: column;
  padding-right: 12px;
  overflow-x: hidden;
  flex: 1;
  &:hover {
    overflow-y: auto;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #6c89b7;
    border-radius: 5px;
  }
  &::-webkit-scrollbar {
    width: 7px;
    border: 1px solid transparent;
  }
`;

interface UpcomingsProps {
  data: IGetUpcominsOutput | undefined,
  loading: boolean,
  error: ApolloError | undefined,
  refetch: () => void,
  selectedDate?: string | null | undefined
  hidePastSelector?: boolean
}

interface IUpcomingCardProps {
  isNewPatient?: boolean,
  name: string,
  dates: string,
  phone: string,
}

const iconStyle = {
  width: '20px',
  height: '20px',
  background: '#FFFFFF',
  border: '3px solid #BCFFEB',
  margin: '10px',
  boxShadow: 'initial'
}

const iconStyleTransparent = {
  width: '20px',
  height: '20px',
  background: 'transparent',
  border: '3px solid transparent',
  margin: '10px',
  boxShadow: 'initial'
}

const contentStyle = {
  background: '#F0F5FD',
  boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  padding: '0px',
}
const contentArrowStyle = {
  borderRight: '7px solid  #F0F5FD',
}
const contentArrowStyleHide = {
  borderRight: '7px solid  transparent',
}

const convertMinsToHrsMins = (mins: number) => {
  let h = Math.floor(mins / 60);
  let m = mins % 60;
  let hh = h < 10 && h > 0 ? `0${h}` : h;
  if (h !== 0) {
    let mm = m < 10 && m > 0 ? `0${Math.abs(m)}` : Math.abs(m);
    return `${hh}:${mm} hours`
  } else {
    let mm = m < 10 && m > 0 ? `0${m}` : m;
    return `${mm} minutes`;
  }
}

const getPhoneNumber = (invitee: IEventInvitees | null) => {
  if (!invitee) return null

  if (invitee.textReminderNumber) return invitee.textReminderNumber

  if (invitee.questionsAndAnswers) {
    const q = invitee.questionsAndAnswers.find(x => x.question === 'Phone number (we will call you)')
    if (q && !!q.answer) {
      return q.answer
    }
  }

  return null
}

const UpcomingCard = ({
  upcoming,
  session
}: { upcoming: ICalendlyEvent, session: IUser }) => {
  const navigate = useNavigate()
  const [startsIn, setStartsIn] = useState<string | null>(null)
  const [hasEnded, setHasEnded] = useState(false)
  const [isInMoreThan24, setIsInMoreThan24] = useState(false)
  const [isViewPatient, setIsViewPatient] = useState<boolean>(false);
  const [isViewTalkingPoints, setIsViewTalkingPoints] = useState<boolean>(false);
  const [isSendSms, setisSendSms] = useState<boolean>(false);

  const [invitee] = useState(upcoming.invitees && upcoming.invitees.length > 0 ? upcoming.invitees[0] : null)
  const [user] = useState(upcoming.invitees && upcoming.invitees.length > 0 ? upcoming.invitees[0].user : null)
  const [canReportNoShow, setCanReportNoShow] = useState(false)
  const [reportNoShow, { loading, data, error }] = useMutation<IReportNoShowOutput, IReportNoShowInput>(REPORT_NO_SHOW, {
    refetchQueries: [
      { query: GET_UPCOMINGS }],
    awaitRefetchQueries: true,
    onCompleted: (e) => {
      if (e.healthCoachReportNoShow && e.healthCoachReportNoShow.ok) {
        toast.success('No show reported', {
          autoClose: 3000
        });
      } else {
        toast.error('No show not reported', {
          autoClose: 3000
        });
      }
    },
    onError: () => {
      toast.error('An error occured', {
        autoClose: 3000
      });
    }
  })
  useEffect(() => {
    getStartsIn()
  }, [])

  useInterval(() => {
    getStartsIn()
  }, 60000)

  const handleReportNoShow = () => {
    if (!invitee || !invitee.uri) return
    reportNoShow({
      variables: {
        inviteeUri: invitee.uri
      }
    })
  }

  const getStartsIn = () => {
    if (moment(upcoming.endTime).isBefore(moment())) {
      setCanReportNoShow(true)
      const b = moment(upcoming.endTime).diff(moment(), 'minutes')
      setHasEnded(true)
      setStartsIn(`Ended ${convertMinsToHrsMins(b * -1)} ago`)
      setIsInMoreThan24(b * -1 >= 1400)
    } else if (moment(upcoming.startTime).isBefore(moment())) {
      const a = moment(upcoming.startTime).diff(moment(), 'minutes')
      const b = moment(upcoming.endTime).diff(moment(), 'minutes')

      setCanReportNoShow(a < -9)
      setIsInMoreThan24(a >= 1400)
      setHasEnded(false)
      setStartsIn(`Started ${convertMinsToHrsMins(a)}`)

    } else {
      const a = moment(upcoming.startTime).diff(moment(), 'minutes')
      const b = moment(upcoming.endTime).diff(moment(), 'minutes')
      setCanReportNoShow(a < -9)
      setIsInMoreThan24(a >= 1400)
      setHasEnded(false)
      setStartsIn(`Starts in ${convertMinsToHrsMins(a)}`)
    }
  }
  let patientName = 'unknown'
  let fullPatientName = 'unknown'
  let homephone: string | null = null

  if (invitee) {
    fullPatientName = invitee.name

    homephone = getPhoneNumber(invitee)
    if (!!user) {
      patientName = `${(user.firstName || '').charAt(0)}. ${user.lastName}`
    } else {
      if (!!invitee.firstName && invitee.lastName) {
        patientName = `${(invitee.firstName || '').charAt(0)}. ${invitee.lastName}`
      } else {
        patientName = fullPatientName
      }
    }
  }

  if (Constants.IS_SCRAMBLING_MODE) {
    patientName = `${getMockedFirstName()} ${getMockedLastName()}`
    fullPatientName = `${getMockedFirstName()} ${getMockedLastName()}`
    homephone = getMockedPhone()
  }

  return (
    <Flex width='100%'>

      <Card style={{ flex: 'initial', opacity: upcoming.status === 'canceled' ? 0.75 : 1, padding: '12px 12px', position: 'relative', overflow: 'hidden', width: '100%' }}>
        <Flex flexDirection={'column'}>

          <Text fontWeight={600} fontSize='18px' mb={2}>{patientName}</Text>
          <Text fontWeight={400} fontSize='14px' mb={2} >{upcoming.name?.replace('Paloma ', '')}</Text>
          <Flex mb={3} alignItems={'center'}>

            <Text fontWeight={400} fontSize='11px' color='rgba(52, 79, 122, 0.6)' mr={2}>{`${moment(upcoming.startTime).format('hh:mmA')} - ${moment(upcoming.endTime).format('hh:mmA')}`}</Text>
            {upcoming.status === 'canceled' && (
              <Box style={{ border: `1px solid #344f79`, borderRadius: '8px', padding: '0 4px', background: 'rgb(121,146, 179)', alignSelf: 'flex-start' }}>
                <Text fontWeight={400} fontSize='12px' color='white'>{`cancelled`}</Text>
              </Box>
            )}
            {!isInMoreThan24 && (
              <>
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 1.125C4.65117 1.125 1.125 4.65117 1.125 9C1.125 13.3488 4.65117 16.875 9 16.875C13.3488 16.875 16.875 13.3488 16.875 9C16.875 4.65117 13.3488 1.125 9 1.125ZM12.1025 11.4205L11.5998 12.1061C11.5889 12.121 11.5751 12.1336 11.5593 12.1432C11.5434 12.1528 11.5259 12.1591 11.5076 12.1619C11.4893 12.1647 11.4706 12.1638 11.4527 12.1594C11.4347 12.1549 11.4178 12.1469 11.4029 12.1359L8.49551 10.016C8.4774 10.003 8.46267 9.98584 8.45258 9.96596C8.44248 9.94607 8.43731 9.92406 8.4375 9.90176V5.0625C8.4375 4.98516 8.50078 4.92188 8.57812 4.92188H9.42363C9.50098 4.92188 9.56426 4.98516 9.56426 5.0625V9.41309L12.0709 11.2254C12.1342 11.2693 12.1482 11.3572 12.1025 11.4205Z" fill="#F8A294" />
                </svg>

                <Text fontWeight={400} fontSize='11px' color='#F8A294' ml={2}>{startsIn}</Text>
              </>
            )}
          </Flex>

          {!!homephone && (
            <Flex width={'100%'} mb={3} justifyContent='center'>
              <Flex flex={1} alignItems='center'>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12.1078 3.14727L10.6518 1.69258C10.549 1.58942 10.427 1.50756 10.2925 1.45172C10.1581 1.39588 10.0139 1.36715 9.86836 1.36719C9.57168 1.36719 9.29278 1.4834 9.0836 1.69258L7.5168 3.25937C7.41364 3.36209 7.33178 3.48417 7.27594 3.61861C7.2201 3.75305 7.19137 3.8972 7.19141 4.04277C7.19141 4.33945 7.30762 4.61836 7.5168 4.82754L8.6625 5.97324C8.39432 6.56436 8.02146 7.10211 7.56192 7.56055C7.10353 8.02121 6.56582 8.39542 5.97461 8.66523L4.82891 7.51953C4.7262 7.41637 4.60411 7.33452 4.46967 7.27867C4.33523 7.22283 4.19108 7.19411 4.04551 7.19414C3.74883 7.19414 3.46992 7.31035 3.26074 7.51953L1.69258 9.08496C1.58929 9.18786 1.50737 9.31017 1.45152 9.44485C1.39568 9.57953 1.36702 9.72393 1.36719 9.86973C1.36719 10.1664 1.4834 10.4453 1.69258 10.6545L3.1459 12.1078C3.47949 12.4428 3.94024 12.6328 4.41328 12.6328C4.51309 12.6328 4.60879 12.6246 4.70313 12.6082C6.5461 12.3047 8.37403 11.3244 9.84922 9.85059C11.3231 8.37813 12.302 6.55156 12.6096 4.70312C12.7025 4.13848 12.5152 3.55742 12.1078 3.14727Z" fill="#344F7A" fillOpacity="0.65" />
                </svg>

                <Text fontWeight={400} color='rgba(52, 79, 122, 0.6)' fontSize='11px' ml={2}>{homephone ? formatPhoneNumber(homephone) : 'Not found'}</Text>

              </Flex>
              <Box style={{ border: '1px solid #CDD9EC', cursor: 'pointer', padding: '4px 12px', borderRadius: '10px' }} mr={'6px'} onClick={() => setisSendSms(true)}>
                <Text fontSize={'11px'} textAlign={'center'}>Send SMS</Text>
              </Box>
            </Flex>
          )}

          {!!canReportNoShow && (
            <Button variant={'pink'} style={{
              padding: '6px 0',
              fontSize: '12px',
              borderRadius: '10px'
            }}
              disabled={!!loading || (!!invitee?.noShow && !!invitee?.noShow.uri)} mr={'6px'} mb={3} onClick={() => {
                if (!!loading || (!!invitee?.noShow && !!invitee?.noShow.uri)) return
                handleReportNoShow()
              }}>{!invitee?.noShow ? !!loading ? 'Reporting No Show...' : 'Report No Show' : 'No Show Reported'}</Button>
          )}
          <Flex>

            {!!user && (
              <Box style={{ border: '1px solid #CDD9EC', cursor: 'pointer', padding: '4px 12px', flex: 1, borderRadius: '10px' }} mr={'6px'} onClick={() => setIsViewPatient(true)}>
                <Text fontSize={'11px'} textAlign={'center'}>View Patient</Text>
              </Box>
            )}
            {!isInMoreThan24 && (
              <>
                {homephone ? (
                  <Box style={{ background: '#344F7A', cursor: 'pointer', padding: '4px 12px', flex: 1, borderRadius: '10px' }} ml={'6px'} onClick={() => {
                    window.open(`dialpad://${homephone?.replace(/[^\d+]/g, '')}?launchMinimode=1&crmLogging=0&showDispositions=1&customData=${encodeURI(upcoming.name || '')}`);
                  }}>
                    <Text fontSize={'11px'} color='white' textAlign={'center'}>Call</Text>
                  </Box>
                ) : (
                  <Text fontSize={'11px'} color='white' textAlign={'center'}>No Phone #</Text>)}
              </>)}
          </Flex>
          {invitee?.questionsAndAnswers && (
            <Text fontSize={'12px'} style={{ color: '#0645AD', cursor: 'pointer' }} mt={2} onClick={() => { setIsViewTalkingPoints(true) }} textAlign={'left'} fontWeight={500}>Review Answers {'>>'}</Text>
          )}

        </Flex>


        <Modal
          isOpen={!!isViewTalkingPoints}
          onBackgroundClick={() => setIsViewTalkingPoints(false)}
          onEscapeKeydown={() => setIsViewTalkingPoints(false)}>
          <Flex
            flexDirection={'column'}
            paddingX={[0, '24px', '24px']}
            style={{
              margin: '0 auto',
              alignSelf: 'center',
              borderRadius: '20px',
              position: 'relative',
              backgroundColor: 'white',
              paddingBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '96%',
              width: '580px',
              maxWidth: '96%',
              overflow: 'hidden',
              paddingTop: '4px',
              paddingRight: 0
            }}>
            <Heading mb={4}>Questions & Answers</Heading>
            {invitee?.questionsAndAnswers?.map(x => {
              return (
                <Flex flexDirection={'column'} mb={3} key={`qa${x.position}`}>
                  <Text fontSize={'14px'} >{x.question}</Text>
                  <Text>{x.answer}</Text>
                </Flex>
              )
            })}
            <Flex justifyContent='flex-end' style={{ borderBottom: '1px solid rgb(229, 233, 236)' }}>
              <Text mb={1} mt={1} mr={2} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setIsViewTalkingPoints(false)}>Close</Text>
            </Flex>
          </Flex>
        </Modal>

        <Modal
          isOpen={!!isViewPatient}
          onBackgroundClick={() => setIsViewPatient(false)}
          onEscapeKeydown={() => setIsViewPatient(false)}>
          <Flex
            flexDirection={'column'}
            paddingX={[0, '24px', '24px']}
            style={{
              margin: '0 auto',
              alignSelf: 'center',
              borderRadius: '20px',
              position: 'relative',
              background: 'linear-gradient(rgb(187, 212, 237) 0%, rgb(253, 245, 242) 100%)',
              paddingBottom: '24px',
              display: 'flex',
              flexDirection: 'column',
              maxHeight: '96',
              height: '96%',
              width: '96%',
              maxWidth: '96%',
              overflow: 'hidden',
              paddingTop: '4px',
              paddingRight: 0
            }}>
            <Flex justifyContent='flex-end' style={{ borderBottom: '1px solid rgb(229, 233, 236)' }}>
              <Text mb={1} mt={1} mr={3} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => navigate(`/patient/${user?.athenaId}`)}>Go to patient</Text>
              <Text mb={1} mt={1} mr={2} style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => setIsViewPatient(false)}>Close</Text>
            </Flex>
            <Flex style={{ overflow: 'scroll', width: '100%', height: '100%' }}>
              <PatientInner id={user?.athenaId || ''} />

            </Flex>
          </Flex>
        </Modal>
        {!!homephone && !!isSendSms && (
          <Modal
            isOpen={!!isSendSms}
            onBackgroundClick={() => setisSendSms(false)}
            onEscapeKeydown={() => setisSendSms(false)}>
            <SendSms id={homephone} name={fullPatientName} close={() => { setisSendSms(false) }} patientId={user?.athenaId || ''}/>
          </Modal>
        )}
      </Card>
    </Flex>
  )
}

const OpenSlotCard = ({
  upcoming
}: { upcoming: ICalendlyEvent }) => {
  return (
    <Flex width='100%' opacity={0.5}>
      <Card style={{ flex: 'initial', padding: '12px 12px', position: 'relative', overflow: 'hidden', width: '100%' }}>
        <Flex flexDirection={'row'} alignItems='center'>
          <Text fontWeight={600} fontSize='18px' flex={1}>{`Open slot`}</Text>
          <Text fontWeight={400} fontSize='11px' color='rgba(52, 79, 122, 0.6)' ml={2}>{`${moment(upcoming.startTime).format('hh:mmA')} - ${moment(upcoming.endTime).format('hh:mmA')}`}</Text>
        </Flex>
      </Card>
    </Flex>
  )
}

const UpcomingCardLoading = () => {
  return (
    <Card style={{ flex: 'initial', padding: '12px 12px' }}>
      <Flex flexDirection={'column'}>
        <Skeleton style={{
          height: '24px',
          width: '72px'
        }} />

        <Text fontWeight={600} fontSize='18px' mb={2} mt={2}><Skeleton style={{ width: '146px' }} /></Text>
        <Flex style={{ width: '100%' }}>

          <Skeleton style={{ width: '124px', height: '16px' }} />

          <Skeleton style={{ width: '124px', marginLeft: '12px' }} />
        </Flex>
        <Flex>
          <svg style={{ opacity: 0, marginRight: '8px' }} width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.1078 3.14727L10.6518 1.69258C10.549 1.58942 10.427 1.50756 10.2925 1.45172C10.1581 1.39588 10.0139 1.36715 9.86836 1.36719C9.57168 1.36719 9.29278 1.4834 9.0836 1.69258L7.5168 3.25937C7.41364 3.36209 7.33178 3.48417 7.27594 3.61861C7.2201 3.75305 7.19137 3.8972 7.19141 4.04277C7.19141 4.33945 7.30762 4.61836 7.5168 4.82754L8.6625 5.97324C8.39432 6.56436 8.02146 7.10211 7.56192 7.56055C7.10353 8.02121 6.56582 8.39542 5.97461 8.66523L4.82891 7.51953C4.7262 7.41637 4.60411 7.33452 4.46967 7.27867C4.33523 7.22283 4.19108 7.19411 4.04551 7.19414C3.74883 7.19414 3.46992 7.31035 3.26074 7.51953L1.69258 9.08496C1.58929 9.18786 1.50737 9.31017 1.45152 9.44485C1.39568 9.57953 1.36702 9.72393 1.36719 9.86973C1.36719 10.1664 1.4834 10.4453 1.69258 10.6545L3.1459 12.1078C3.47949 12.4428 3.94024 12.6328 4.41328 12.6328C4.51309 12.6328 4.60879 12.6246 4.70313 12.6082C6.5461 12.3047 8.37403 11.3244 9.84922 9.85059C11.3231 8.37813 12.302 6.55156 12.6096 4.70312C12.7025 4.13848 12.5152 3.55742 12.1078 3.14727Z" fill="#344F7A" fillOpacity="0.65" />
          </svg>
          <Skeleton style={{ width: '86px' }} />
        </Flex>
        <Flex style={{ width: '100%' }}>
          <Skeleton style={{ marginRight: '8px', width: '130px', height: '27px' }} />
          <Skeleton style={{ marginLeft: '8px', width: '130px', height: '27px' }} />
        </Flex>
      </Flex>
    </Card>)
}

export const UpcomingsWithSession: React.FC<UpcomingsProps & { session: IUser }> = ({ data, loading, error, refetch, session, selectedDate, hidePastSelector = false }) => {
  const [hidePast, setHidePast] = useState(selectedDate === moment().format('MM/DD/YYYY'))

  useEffect(() => {
    setHidePast(selectedDate === moment().format('MM/DD/YYYY'))
  }, [selectedDate])

  return (
    <Flex flexDirection={'column'} mr={3} minWidth="300px" overflow={"hidden"}>
      <Flex overflow={"hidden"}>
        <Heading style={{ fontSize: '24px' }} flex={1}>{`${moment().isSameOrBefore(moment(selectedDate), 'd') ? 'Upcoming' : 'Past'} ${selectedDate === moment().format('MM/DD/YYYY') ? '(today)' : `(${selectedDate})`}`}</Heading>
        <ToolbarMenuButton data-tip='Refresh upcoming' onClick={refetch} background='transparent'>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 11H7.101L7.102 10.991C7.23257 10.3516 7.48813 9.74434 7.854 9.20399C8.39845 8.4018 9.16215 7.77315 10.054 7.39299C10.356 7.26499 10.671 7.16699 10.992 7.10199C11.6579 6.96698 12.3441 6.96698 13.01 7.10199C13.967 7.29808 14.8451 7.7714 15.535 8.46299L16.951 7.05099C16.3128 6.41262 15.5578 5.90303 14.727 5.54999C14.3033 5.37062 13.8628 5.23394 13.412 5.14199C12.4818 4.953 11.5232 4.953 10.593 5.14199C10.1419 5.23432 9.70101 5.37133 9.277 5.55099C8.02753 6.08109 6.95793 6.96108 6.197 8.08499C5.68489 8.84284 5.32676 9.69398 5.143 10.59C5.115 10.725 5.1 10.863 5.08 11H2L6 15L10 11ZM14 13H16.899L16.898 13.008C16.6367 14.2897 15.8812 15.4171 14.795 16.146C14.2548 16.5122 13.6475 16.7677 13.008 16.898C12.3424 17.033 11.6566 17.033 10.991 16.898C10.3516 16.7674 9.74435 16.5119 9.204 16.146C8.93862 15.9665 8.69085 15.7622 8.464 15.536L7.05 16.95C7.68851 17.5882 8.44392 18.0974 9.275 18.45C9.699 18.63 10.142 18.767 10.59 18.858C11.5198 19.0471 12.4782 19.0471 13.408 18.858C15.2005 18.4859 16.7773 17.4294 17.803 15.913C18.3146 15.1557 18.6724 14.3053 18.856 13.41C18.883 13.275 18.899 13.137 18.919 13H22L18 8.99999L14 13Z" fill="black" />
          </svg>
        </ToolbarMenuButton>
      </Flex>

      <Flex flex={
        !loading &&
          ((!data
            || !data.healthCoachGetUpcomings
            || !data.healthCoachGetUpcomings.events
            || data.healthCoachGetUpcomings.events.length < 1)
            || data.healthCoachGetUpcomings.events.filter(x => {
              if (selectedDate !== moment().format('MM/DD/YYYY')) return true

              if (!!hidePast) {
                if (moment(x.endTime).isBefore(moment())) return false
              }
              return true
            }).length < 1) ? 'initial' : 1} overflow={'hidden'}>
        <Wrapper id='wrpUps'>
          {!!loading && (
            [1, 2, 3].map(x => {
              return <UpcomingCardLoading key={`loadingUpcomings${x}`} />
            })
          )}
          {!loading && data?.healthCoachGetUpcomings && data?.healthCoachGetUpcomings.events && data?.healthCoachGetUpcomings.events.length > 0 &&
            (
              <VerticalTimeline layout='1-column' lineColor='#344F7A' >
                {(!selectedDate || selectedDate === moment().format('MM/DD/YYYY')) && !hidePastSelector && (
                  <VerticalTimelineElementCustomFilter
                    className="vertical-timeline-element--work"
                    contentStyle={contentStyle}
                    contentArrowStyle={contentArrowStyleHide}
                    iconStyle={iconStyleTransparent}
                  >
                    <Flex style={{ width: '100%', border: '1px solid transparent', borderRadius: '10px', padding: '4px', alignItems: 'center' }}>
                      <Text flex={1} fontSize='12px'>Past calls</Text>
                      <Text style={{ fontSize: '12px' }} mr={2}>Show</Text>
                      <Toggle
                        checked={hidePast}
                        icons={false}
                        onChange={(e) => setHidePast(e.target.checked)}
                      />
                      <Text style={{ fontSize: '12px' }} ml={2}>Hide</Text>
                    </Flex>
                  </VerticalTimelineElementCustomFilter>
                )}
                {data?.healthCoachGetUpcomings.events.filter(x => {
                  if (selectedDate !== moment().format('MM/DD/YYYY')) return true

                  if (!!hidePast) {
                    if (moment(x.endTime).isBefore(moment())) return false
                  }
                  return true
                }).map(x => {
                  return (
                    <VerticalTimelineElementCustom
                      className="vertical-timeline-element--work"
                      contentStyle={contentStyle}
                      contentArrowStyle={contentArrowStyle}
                      iconStyle={iconStyle}

                      key={`aid${x.uri}`}
                    >
                      <UpcomingCard upcoming={x} session={session} />
                    </VerticalTimelineElementCustom>)
                })}
              </VerticalTimeline>
            )}
        </Wrapper>
      </Flex>
      
      {!loading && ((!data || !data.healthCoachGetUpcomings || !data.healthCoachGetUpcomings.events || data.healthCoachGetUpcomings.events.length < 1) || data.healthCoachGetUpcomings.events.filter(x => {
        if (selectedDate !== moment().format('MM/DD/YYYY')) return true

        if (!!hidePast) {
          if (moment(x.endTime).isBefore(moment())) return false
        }
        return true
      }).length < 1) && (
          <Flex flexDirection={'column'} width={'100%'} justifyContent='flex-start' alignItems={'center'}>
            <NoUpcomings style={{ maxWidth: '150px', maxHeight: '207.6px' }} />
            <Text fontWeight={300} textAlign='center'>No {moment().isSameOrBefore(moment(selectedDate), 'd') ? 'upcoming' : 'past'} appointments or slots<br />on {moment(selectedDate).format('MMM DD, YYYY')}</Text>
          </Flex>
        )}
      <ReactTooltip place="bottom" type="dark" effect="float" />
    </Flex>);
}

export const Upcomings: React.FC<UpcomingsProps> = withSession(UpcomingsWithSession)